'use strict';
import Loader from '@components/Loading/Loader.js';
import UIIcon from '@icons/UIIcon.js';
import ProjectsState, { LoadedFolderIds } from '@states/Projects.js';
import { AvailableModelState, CadFileState } from '@states/Viewer.js';
import { UIButton, UIDiv, UIImage, UIText } from '@ui';
import { getCadFileModelId, getReconstructionId } from '@utils/cadFileModel.js';
import { getFolderIdFromUrl, getSharingId } from '@utils/projectUtils.js';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'];

const getExtension = value =>
  String(value || '')
    .split('?')[0]
    .split('#')[0]
    .split('.')
    .pop()
    .toLowerCase();

const isImageReconstruction = reconstruction =>
  IMAGE_EXTENSIONS.includes(
    getExtension(
      reconstruction.filePath ||
        reconstruction.fileName ||
        reconstruction.fileOriginalName,
    ),
  );

class AiAnalyzerQuickImageList extends UIDiv {
  constructor({ getSelection, onSelect } = {}) {
    super();
    this.setClass('hidden w-full max-w-lg mb-3');
    this.getSelection = getSelection;
    this.onSelect = onSelect;
    this.isLoading = false;
    this.isDestroyed = false;
    this.filesList = null;
    this.unsubscribeProjects = ProjectsState.subscribe(() => this.#build());
    this.unsubscribeCadFile = CadFileState.subscribe(() => this.#build());

    this.#build();
  }

  destroy() {
    this.isDestroyed = true;
    this.filesList = null;
    if (this.unsubscribeProjects) {
      this.unsubscribeProjects();
      this.unsubscribeProjects = null;
    }
    if (this.unsubscribeCadFile) {
      this.unsubscribeCadFile();
      this.unsubscribeCadFile = null;
    }
  }

  refreshSelection() {
    this.#build();
  }

  async #ensureProjectTasksLoaded(projectId) {
    if (
      !projectId ||
      this.isLoading ||
      LoadedFolderIds.loadedFolderIds?.includes(projectId)
    ) {
      return;
    }

    this.isLoading = true;
    this.#renderLoading();
    try {
      await ProjectsState.getProjectById(projectId);
    } finally {
      this.isLoading = false;
    }
  }

  async #build() {
    if (this.isDestroyed) return;

    const context = this.#getContext();

    if (context.projectId && !context.sharingId) {
      await this.#ensureProjectTasksLoaded(context.projectId);
      if (this.isDestroyed) return;
    }

    const files = this.#getFilesForContext(context);

    if (!files.length) {
      this.clear();
      this.filesList = null;
      this.setClass('hidden w-full max-w-lg mb-3');
      return;
    }

    this.#renderFiles(files, context);
  }

  #getContext() {
    return {
      sharingId: getSharingId(),
      projectId: getFolderIdFromUrl(),
    };
  }

  #getFilesForContext({ sharingId, projectId }) {
    if (sharingId) {
      const task =
        (ProjectsState.projects.tasks || []).find(
          item => item.sharingId === sharingId,
        ) || this.#cadFileToTask(CadFileState.cadfile);
      return task ? this.#getImageFilesForTasks([task]) : [];
    }

    if (!projectId) return [];

    const tasks = (ProjectsState.projects.tasks || []).filter(
      task => String(task.parentId) === String(projectId),
    );
    return this.#getImageFilesForTasks(tasks);
  }

  #cadFileToTask(cadfile) {
    if (!cadfile) return null;
    return {
      id: cadfile.id || cadfile.cadFileID || cadfile.cadFileId,
      name: cadfile.name || 'Untitled task',
      sharingId: cadfile.sharingId,
      parentId: cadfile.parentId || cadfile.model_folder?.id,
      reconstructions: cadfile.reconstructions || [],
    };
  }

  #getImageFilesForTasks(tasks) {
    const files = [];
    tasks.forEach(task => {
      (task.reconstructions || [])
        .filter(isImageReconstruction)
        .forEach(reconstruction => {
          const path = reconstruction.filePath;
          if (!path) return;
          const viewerModel =
            this.#viewerModelForReconstruction(reconstruction);
          files.push({
            taskId: task.id,
            cadFileModelId:
              getCadFileModelId(reconstruction) ||
              getCadFileModelId(viewerModel),
            reconstructionId:
              getReconstructionId(reconstruction) ||
              getReconstructionId(viewerModel),
            taskName: task.name || 'Untitled task',
            path,
            thumbnail: reconstruction.thumbnail || path,
            originalName:
              reconstruction.fileOriginalName ||
              reconstruction.fileName ||
              path.split('/').pop() ||
              'image',
            fileSize: reconstruction.fileSize,
          });
        });
    });
    return files;
  }

  #viewerModelForReconstruction(reconstruction) {
    const reconstructionId = getReconstructionId(reconstruction);
    const path = reconstruction?.filePath;
    return (
      (AvailableModelState.availableModel || []).find(model => {
        const sameReconstruction =
          reconstructionId &&
          String(getReconstructionId(model)) === String(reconstructionId);
        const samePath = path && model?.filePath === path;
        return sameReconstruction || samePath;
      }) || null
    );
  }

  #renderLoading() {
    this.clear();
    this.filesList = null;
    this.setClass('w-full max-w-lg mb-3');
    this.add(
      new UIDiv()
        .setClass('flex items-center justify-center py-3')
        .add(new Loader()),
    );
  }

  #renderFiles(files, context) {
    const scrollParent = this.#getScrollParent();
    const scrollTop = scrollParent?.scrollTop || 0;
    const scrollLeft = this.filesList?.dom?.scrollLeft || 0;

    this.clear();
    this.setClass('w-full max-w-lg mb-3');

    const selection = this.getSelection?.() || {};
    const selectedImages =
      selection.images?.length > 0
        ? selection.images
        : selection.image
          ? [selection.image]
          : [];

    const title = new UIText(
      context.sharingId
        ? i18next.t('sidebar.aiAnalyzer.quick_images.current_task')
        : i18next.t('sidebar.aiAnalyzer.quick_images.current_project'),
    );
    title.setClass('text-sm font-semibold text-slate-700 mb-2 block');

    const list = new UIDiv();
    list.setClass('flex max-w-full flex-nowrap gap-2 overflow-x-auto pb-1');
    this.filesList = list;

    files.forEach(file => {
      const isSelected = selectedImages.some(
        image =>
          image?.path === file.path &&
          String(image?.taskId) === String(file.taskId),
      );
      list.add(this.#buildItem(file, isSelected));
    });

    this.add(title, list);
    list.dom.scrollLeft = scrollLeft;
    if (scrollParent) {
      scrollParent.scrollTop = scrollTop;
    }
  }

  #getScrollParent() {
    let element = this.dom.parentElement;
    while (element) {
      const style = window.getComputedStyle(element);
      if (/(auto|scroll)/.test(`${style.overflowY}${style.overflow}`)) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }

  #buildItem(file, isSelected) {
    const button = new UIButton();
    button.setClass(
      [
        'group relative flex w-28 shrink-0 flex-col overflow-hidden rounded-md border bg-white text-left transition-colors hover:bg-slate-50',
        isSelected
          ? 'border-[var(--o-hive-primary-80)] ring-2 ring-[var(--o-hive-primary-20)]'
          : 'border-slate-200 hover:border-slate-300',
      ].join(' '),
    );
    button.setAttribute('title', file.originalName);

    const thumbnailWrapper = new UIDiv();
    thumbnailWrapper.setClass('relative h-16 w-full bg-slate-100');

    const thumbnail = new UIImage(file.thumbnail);
    thumbnail.setAlt(file.originalName);
    thumbnail.setClass('h-full w-full object-cover');
    thumbnail.dom.onerror = () => {
      thumbnail.setSrc('/static/images/icons/image.svg');
    };
    thumbnailWrapper.add(thumbnail);

    if (isSelected) {
      thumbnailWrapper.add(
        new UIDiv()
          .setClass(
            'absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--o-hive-primary-80)] text-white',
          )
          .add(
            new UIIcon('check', {
              color: 'none',
              strokeColor: 'currentColor',
              size: 14,
            }),
          ),
      );
    }

    const text = new UIDiv();
    text.setClass('min-w-0 p-1.5');
    text.add(
      new UIText(file.originalName).setClass(
        'block truncate text-xs font-medium text-slate-700',
      ),
      new UIText(file.taskName).setClass(
        'block truncate text-[11px] text-slate-500',
      ),
    );

    button.add(thumbnailWrapper, text);
    button.onClick(e => {
      e.stopPropagation();
      this.onSelect?.({ ...file, source: 'project' });
    });

    return button;
  }
}

export { AiAnalyzerQuickImageList };
